import React, { useEffect, useState } from "react";
import {
  Loader2,
} from "lucide-react";
import { VideoType } from "../../store/adsPageStore";
import { NarrativeContentStyleTypeTranslateToSpanish } from "../../types/Narratives/NarrativesTypes";
import { useAuth } from "../../context/AuthContext";
import { dictionary } from "../../utils/dictionary";
import NarrativeMdOutut from "./NarrativeMdOutut";
import VideoPlayer from "./VideoPlayer";
import { useVideo, Video } from "../../context/VideoContext";
import { getAvatars, getVideoById, postVideo, PostVideo } from "../../services/ads/videos/videosService";
import toast from "react-hot-toast";
import { AvatarSelector } from "../modals/AvatarSelector";
import { VideoStatus } from "../../services/interface";
import { CreditsBadge } from "../ui/badge/CreditBadge";
import { membershipAxios } from "../../services/membership/MembershipAxios";
import { useWebsitesStore } from "../../store/websitesStore";
import { useNavigate } from "react-router-dom";
import { IconCredit1 } from "../../assets/icons/icons";
import { promptGeneratorJson } from "../../services/conversational/ads";
import { handleApi } from "../../utils/handleApi";
import Loader from "../common/Loader";

interface NarrativeOutputProps {
  getListVideosService: () => void;
  narrativeId: string;
  productId: string;
  narrative: string;
  style: string;
  title: string;
  videoType: VideoType;
  is_recorded?: boolean;
  hideActions?: boolean;
  embedded?: boolean;
}

export const NarrativeOutput: React.FC<NarrativeOutputProps> = ({
  getListVideosService,
  narrativeId,
  narrative,
  productId,
  style,
  title,
  videoType,
  is_recorded,
  hideActions,
  embedded,
}) => {
  const { language, credits, setCredits } = useAuth();
  const [loaderCreateAd, setLoaderCreateAd] = useState(false);
  const [openVideoModal, setOpenVideoModal] = useState(false);
  const [openSpaceVideo, setOpenSpaceVideo] = useState(false);
  const [video, setVideo] = useState<Video | undefined>(undefined);
  const { videos, setVideos } = useVideo();
  const [adSelectedCreated, setAdSelectedCreated] = useState<string | undefined>(undefined);
  const [avatars, setAvatars] = useState<any[]>([]);
  const { projectActive } = useWebsitesStore();
  const [projectId, setProjectId] = useState<string | null>(null);
  const [updateCredits, setUpdateCredits] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      const projectId = await projectActive();
      setProjectId(projectId);
    })();
  }, []);

  const setCreditsToContext = async () => {
    try {
      const membership = await membershipAxios();
      setCredits(membership.credits || 0);
    } catch (error: any) {
      console.error("Error fetching membership:", error);
      if (error.response?.data.trigger === "NOT_ACTIVE_MEMBERSHIP") {
        navigate("/plans");
      }
    }
  };

  useEffect(() => {
    setCreditsToContext();
  }, []);

  useEffect(() => {
    setOpenSpaceVideo(false);
    const video: Video | undefined = videos.find((video) => video.ad_id === narrativeId);
    if (video !== undefined) {
      setVideo(video);
      setOpenSpaceVideo(true);
    }
  }, [videos, narrativeId]);

  const getTranslatedStyle = (style: string): string => {
    const upperStyle = style.toUpperCase();

    if (upperStyle in NarrativeContentStyleTypeTranslateToSpanish) {
      return NarrativeContentStyleTypeTranslateToSpanish[upperStyle as keyof typeof NarrativeContentStyleTypeTranslateToSpanish];
    }

    return style
      .split("-")
      .map((word, index) =>
        index === 0
          ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
          : word.toLowerCase()
      )
      .join(" ");
  };

  const capitalizedStyle = getTranslatedStyle(style);

  const onCreateAd = () => {
    if (productId === "") {
      toast.error(dictionary["error-ad-creation"][language]);
      return;
    }
    setAdSelectedCreated(narrativeId);
    setOpenVideoModal(true);


    getAvatars()
      .then((res) => {
        setAvatars(res.data);
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        setLoaderCreateAd(false);
      });
  }

  const getVideoByIdTemporal = async (id: string) => {
    await new Promise(resolve => setTimeout(resolve, 10000));
    const res = await getVideoById(id)
    setVideos(videos.map((video) => video.id === res.data.id ? res.data : video));
    // setVideo(res.data);
    return res.data
  }

  useEffect(() => {
    if (video && (video?.state === VideoStatus.DONE || video?.state === VideoStatus.FAILED)) {
      return;
    }
    if (video && (video?.state === VideoStatus.INITIALIZING || video?.state === VideoStatus.IN_PROGRESS)) {
      getVideoByIdTemporal(video?.id.toString());
    }
  }, [video]);

  const generateNewText = async (adText: string) => {
    const { res, error } = await handleApi(promptGeneratorJson(adText));
    if (error) {
      console.error(error);
      return;
    }
    const newText = `${res?.gender} ${res?.ad_text}`;
    setUpdateCredits(true);
    setTimeout(async () => {
      await setCreditsToContext();
      setUpdateCredits(false);
    }, 3000);
    return newText;
  }

  const handleAvatarSelection = async (avatar: any) => {
    const data: PostVideo = {
      avatar_url: avatar.avatar_url,
      product_id: productId,
      ad_text: `${avatar.gender} ${narrative}`,
      ad_id: narrativeId,
    }

    setLoaderCreateAd(true);
    const newText = await generateNewText(data.ad_text);
    if (newText) {
      data.ad_text = newText;
    }

    postVideo(data)
      .then(() => {
        // setIdVideoTemporal(res.data.id);
        setOpenVideoModal(false);
        setOpenSpaceVideo(true);
        getListVideosService();
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        setLoaderCreateAd(false);
      });
  }

  const isVideoEnabled = import.meta.env.VITE_ENABLED_VIDEO === 'true';

  return (
    <div className={embedded ? "mx-4 flex flex-col py-2" : "mx-4 md:mx-8 min-h-[100vh] flex flex-col my-6"}>
      <div className={embedded ? "px-4 py-2 flex-1" : "p-6 flex-1"}>
        <div className="flex flex-row gap-4 lg:items-center justify-between">

          <div className="flex flex-col gap-4 md:flex-row md:items-start justify-between space-y-4 md:space-y-0 md:space-x-4">
            <div className="flex-1 space-y-4">
              <div className="flex items-center space-x-3">
                <h2 className="text-xl font-semibold text-gray-900 leading-tight">
                  {title}
                </h2>
              </div>

              <div className="flex flex-wrap gap-2">
                <span className="inline-flex items-center px-4 py-2 rounded-full text-[12px] font-small bg-white text-gray-700 border border-gray shadow-sm h-[30px]">
                  {dictionary["style"][language]}: {capitalizedStyle}
                </span>
              </div>
            </div>
          </div>

          {isVideoEnabled && !hideActions && <div className="flex items-center justify-center">
            {updateCredits ? <div className="flex items-center justify-center gap-2 mr-4"><Loader size={24} /></div> : <CreditsBadge amount={credits} label={dictionary["credits"][language]} />}
            <button
              disabled={loaderCreateAd}
              onClick={onCreateAd}
              className={`inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-indigo-600 to-indigo-400 text-white rounded-full hover:from-indigo-700 hover:to-indigo-400 text-white font-semibold text-base rounded-full shadow-lg hover:shadow-xl transition-all duration-200 ease-in-out transform hover:scale-105 active:scale-95 ${loaderCreateAd && adSelectedCreated === narrativeId ? 'w-[100px]' : 'w-[210px]'}`}
            >
              <span className="flex items-center justify-center text-sm gap-1">
                +2
                <IconCredit1 className="w-4 h-4" color="#ffffff" />
              </span>
              {/* <Sparkles className="w-5 h-5" /> */}
              <span className="flex items-center justify-center h-[20px]">
                {loaderCreateAd && adSelectedCreated === narrativeId ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  dictionary["create-ad"][language]
                )}
              </span>
            </button>
          </div>}
        </div>

        {
          openVideoModal && (
            <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50">
              <div className="bg-white rounded-2xl shadow-lg w-full max-w-md p-6 flex flex-col gap-2 items-center justify-center">
                {loaderCreateAd || avatars.length === 0 ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span className="text-sm">
                      {dictionary["generating-video"][language]}
                    </span>
                  </>
                ) : (
                  <div className="w-full">
                    <div className="bg-card rounded-lg">
                      <AvatarSelector avatars={avatars} onSelectAvatar={handleAvatarSelection} setOpenVideoModal={setOpenVideoModal} />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )
        }

        {
          openSpaceVideo && (
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 mt-6 flex items-center gap-2 justify-center">
              {loaderCreateAd || video === undefined ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span className="text-sm">
                    {dictionary["generating-video"][language]}
                  </span>
                </>
              ) : (
                <VideoPlayer video={video} onCreateAd={onCreateAd} updateInfoVideo={getVideoByIdTemporal} />
              )}
            </div>
          )}

        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 mt-6">
          <NarrativeMdOutut narrative={narrative} />
        </div>
      </div>
    </div>
  );
};
